/**
 * The option shape every select in the application renders (`docs/select-inputs.md` §3).
 *
 * Three rules are encoded in the type itself:
 *
 * - **`value` is the domain identifier**, never a label, a slug derived from one or a row index: it is the business key
 *   the write endpoints accept, and it is what a stored selection restores from.
 * - **`label` is display text for one locale**, so switching locale re-renders the label without losing the selection.
 * - **`searchText` and `description` widen the local filter** rather than the visible label: an English title is
 *   searchable on a Persian layout (`docs/select-inputs.md` §2), and a description is matched but not shown.
 */

import { matchesSearchTerm } from '@/components/ui/persian-search';

/** One choice of a select control. */
export interface SelectOption {
  /** Domain identifier the option stands for; this is what the control reports. */
  value: string;
  /** Visible text, in the active locale. */
  label: string;
  /** Further text the local filter matches (the other locale's title, an identifier, a family). */
  searchText?: string | null;
  /** Further text the local filter matches but the menu does not render. */
  description?: string | null;
  /**
   * Whether the option represents a stored selection the list no longer offers.
   *
   * Such a row is selectable — it *is* the current value — but it is marked unavailable, so a user does not read it as a
   * normal choice, and it is filtered out of anyone else's list (`docs/select-inputs.md` §3).
   */
  isUnavailable?: boolean;
}

/**
 * Matcher of `react-select`'s local filter for these options.
 *
 * The term is matched against the visible label **and** the other locale's title when the option carries one, plus the
 * description when it has one, because `docs/select-inputs.md` §2 requires a term in either language to find an option —
 * a Persian user still types an English model name, and an English user still types a Persian title.
 *
 * @param option Option the control is filtering.
 * @param term Term the user typed.
 * @returns `true` when the option is offered.
 */
export function matchesOptionTerm(option: SelectOption, term: string): boolean {
  return matchesSearchTerm(term, option.label, option.searchText, option.description ?? undefined);
}

/**
 * Identifier of the element that reads out a locked control's chosen value.
 *
 * A locked `SelectControl` keeps its name through `aria-label` and announces its value from this element. The identifier
 * is the contract between the control and the suites that assert a locked field (`docs/select-inputs.md` §9, "a locked
 * control cannot be found by role-and-name in a jsdom test"), so it lives in this module rather than beside the component:
 * a component module exports components only (`react-refresh/only-export-components`).
 *
 * @param inputId Identifier of the control's search input.
 * @returns The identifier of the element rendering the chosen value for a disabled control.
 */
export function disabledValueId(inputId: string): string {
  return `${inputId}-disabled-value`;
}
